/**
 * Voucher form: locked fields and update payload helpers.
 */

export const EDIT_LOCKED_FIELDS = [
  'code',
  'discountType',
  'discount_value',
  'discount_type',
  'restaurant_id',
  'created_at',
  'id',
  'valid_from',
  'validFrom',
] as const;

export type LockedFieldKey =
  | 'code'
  | 'campaignName'
  | 'discountType'
  | 'discountValue'
  | 'minimumSpend'
  | 'maxDiscountAmount'
  | 'validFrom'
  | 'validUntil'
  | 'initialStatus';

/**
 * Determine which form fields should be locked when editing.
 * - Always locked: code, discountType
 * - Lock discountValue if has redemptions (affects discount logic)
 * - Lock validFrom if voucher has been active or has redemptions
 */
export function getLockedFieldsForEdit(voucher: {
  valid_from: string;
  valid_until: string;
  total_used?: number;
  is_active?: boolean;
}): Set<LockedFieldKey> {
  const locked = new Set<LockedFieldKey>(['code', 'discountType']);
  const now = Date.now();
  const start = new Date(voucher.valid_from).getTime();
  const hasRedemptions = (voucher.total_used ?? 0) > 0;
  const hasBeenActive = !Number.isNaN(start) && now >= start;

  if (hasBeenActive || hasRedemptions) {
    locked.add('validFrom');
  }
  if (hasRedemptions) {
    locked.add('discountValue');
  }

  return locked;
}

/**
 * Build update payload from form values, excluding locked fields.
 */
export function buildVoucherUpdatePayload(
  values: Record<string, unknown>,
  lockedFields: ReadonlySet<string> | ReadonlySet<LockedFieldKey>
): Record<string, unknown> {
  const payload: Record<string, unknown> = {};

  const fieldMap: Record<string, string> = {
    campaignName: 'name',
    discountType: 'discount_type',
    discountValue: 'discount_value',
    minimumSpend: 'minimum_order_amount',
    maxDiscountAmount: 'max_discount_amount',
    validFrom: 'valid_from',
    validUntil: 'valid_until',
  };

  const editableKeys = [
    'campaignName',
    'description',
    'discountType',
    'discountValue',
    'minimumSpend',
    'maxDiscountAmount',
    'validFrom',
    'validUntil',
  ];

  for (const key of editableKeys) {
    if (lockedFields.has(key as LockedFieldKey)) continue;
    const val = values[key];
    if (val === undefined) continue;
    const apiKey = fieldMap[key] || key;
    if (key === 'validFrom' || key === 'validUntil') {
      if (val == null) continue;
      if (typeof val === 'string' && val.length > 0) {
        payload[apiKey] = val;
      } else if (val && typeof val === 'object') {
        const d = val as { toISOString?: () => string };
        if (typeof d.toISOString === 'function') {
          payload[apiKey] = d.toISOString();
        } else {
          try {
            payload[apiKey] = new Date(String(val)).toISOString();
          } catch {
            payload[apiKey] = String(val);
          }
        }
      }
    } else if (key === 'campaignName') {
      payload[apiKey] = String(val).trim();
    } else if (key === 'maxDiscountAmount') {
      const n = Number(val);
      payload[apiKey] = val === '' || val == null || Number.isNaN(n) ? null : n;
    } else {
      payload[apiKey] = val;
    }
  }

  return payload;
}
